// Duration write. Eager on the app shell: the delegate keeps the same import
// position so the door stays where this module already sat.
// Serialized so a retried stale PATCH cannot land after the flush PATCH.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { logger, sanitizeError } from '@heidi/runtime/logger';

import {
  GET_SESSION_BY_ID,
  UPDATE_SESSION_DURATION,
  type QuerySessionResponse,
  type SessionResponse,
} from '../../contracts';
import { updateSessionById } from '../api/session';
import { notFoundSessionIds } from '../query/notFoundSessionIds';

type Props = {
  sessionId: string;
  duration: number;
};

type UseMutateUpdateSessionDurationProps = {
  /** `useIsOfflineConnectivity()`, sampled by the delegate at render. */
  isOffline: boolean;
  /** `canPersistSessionToServer`. Gates the server write, not the cache write. */
  canPersist: (session: QuerySessionResponse | undefined, isOffline: boolean) => boolean;
  /** `MAX_QUERY_RETRIES`. */
  retry: number;
  /** True when this failure may be retried (`!isSessionNotFoundError`). */
  shouldRetry: (error: unknown) => boolean;
  /** Fired for a non-404 failure, where the destructive toast fired. */
  onDurationError?: (error: Error) => void;
};

export function useMutateUpdateSessionDuration({
  isOffline,
  canPersist,
  retry: maxRetries,
  shouldRetry,
  onDurationError,
}: UseMutateUpdateSessionDurationProps) {
  const queryClient = useQueryClient();
  const getSessionKey = GET_SESSION_BY_ID;

  return useMutation({
    retry: (failureCount, error) => shouldRetry(error) && failureCount < maxRetries,
    mutationKey: [UPDATE_SESSION_DURATION],
    scope: { id: UPDATE_SESSION_DURATION },
    mutationFn: async ({ sessionId, duration }: Props) => {
      const session = queryClient.getQueryData<QuerySessionResponse>([
        getSessionKey,
        { sessionId },
      ]);
      if (!canPersist(session, isOffline)) {
        return;
      }
      return await updateSessionById(sessionId, {
        duration: duration,
      });
    },
    onMutate: ({ sessionId, duration }) => {
      queryClient.setQueryData<SessionResponse>(
        [getSessionKey, { sessionId: sessionId }],
        (oldData) => (oldData ? { ...oldData, duration: duration } : undefined)
      );
    },
    onSuccess: (result) => {
      const data = result?.data;
      if (!data) return;

      queryClient.setQueryData<SessionResponse>(
        [getSessionKey, { sessionId: data.session_id }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          const newData: SessionResponse = {
            ...oldData,
            duration: data.duration,
          };
          return newData;
        }
      );
    },
    onError: (error, variables) => {
      // `shouldRetry` is false exactly for session-not-found. That path logs
      // once unless the circuit breaker already recorded the id, and never toasts.
      if (!shouldRetry(error)) {
        if (!notFoundSessionIds.has(variables.sessionId)) {
          logger.warn(
            'Session not found when updating duration, session may have been deleted',
            { sessionId: variables.sessionId }
          );
        }
        return;
      }

      logger.error('Error:', { error: sanitizeError(error) });
      onDurationError?.(error);
    },
  });
}
